// Widget registry — the source of truth for every widget the LayoutRenderer
// knows how to mount. Built-in widgets are registered at module import time;
// plugin widgets call registerWidget() at boot (Phase L5).
//
// Each entry shape:
//   {
//     type:           'home.cases_grid',
//     categories:     ['home'],                  // which Layout categories may use this widget
//     palette_label:  'Cases grid',              // shown in the Designer palette
//     description:    'Paginated grid of cases.',
//     Component:      ReactComponent,            // (props) => JSX  ; props = {config, ctx, children}
//     configSchema:   { fields: [...] } | null,  // xavvy form JSON, drives the property panel
//     publishes:      ['xv.CaseSelected'],       // event topics this widget broadcasts
//     subscribes:     ['xv.RefreshContext'],     // event topics this widget listens for
//     actions:        [{ name, label?, description? }],  // imperative entry points wires can target
//     isContainer:    false,                     // true when widget renders children from the tree
//     group:          'Home',                    // palette grouping (defaults to first category title-cased)
//   }
//
// `actions` are imperative handlers a widget exposes to the wire executor
// (Phase L4a). At mount, the widget calls `useWidgetActions(node.id, {...})`
// to register handlers; a wire of shape
// `{from: {widget_id, topic}, to: {widget_id, action}}` then fires the named
// handler when the source widget publishes the topic. Widgets without
// actions can still publish or subscribe to topics directly via `ctx.bus`.

const REGISTRY = new Map();

export const VALID_CATEGORIES = Object.freeze([
  'home',
  'case',
  'task',
  'add_case',
  'add_task',
]);

export function registerWidget(meta) {
  if (!meta || typeof meta !== 'object') {
    throw new Error('[widgetRegistry] registerWidget: meta object required');
  }
  if (!meta.type || typeof meta.type !== 'string') {
    throw new Error('[widgetRegistry] registerWidget: meta.type required');
  }
  if (REGISTRY.has(meta.type)) {
    // Plugins can hot-reload; allow re-registration but warn so duplicate
    // builtin imports surface in dev.
    if (typeof console !== 'undefined') {
      console.warn(`[widgetRegistry] re-registering widget type "${meta.type}"`);
    }
  }
  const normalized = {
    type: meta.type,
    categories: Array.isArray(meta.categories) && meta.categories.length
      ? meta.categories.slice()
      : VALID_CATEGORIES.slice(),
    palette_label: meta.palette_label || meta.type,
    description: meta.description || '',
    Component: meta.Component || null,
    configSchema: meta.configSchema || null,
    publishes: Array.isArray(meta.publishes) ? meta.publishes.slice() : [],
    subscribes: Array.isArray(meta.subscribes) ? meta.subscribes.slice() : [],
    actions: Array.isArray(meta.actions)
      ? meta.actions
          .filter(a => a && typeof a === 'object' && typeof a.name === 'string' && a.name)
          .map(a => ({ name: a.name, label: a.label || a.name, description: a.description || '' }))
      : [],
    isContainer: !!meta.isContainer,
    group: meta.group || (meta.categories && meta.categories[0]) || 'Other',
  };
  REGISTRY.set(meta.type, normalized);
  return normalized;
}

export function getWidget(type) {
  return REGISTRY.get(type) || null;
}

export function listWidgets(filter = {}) {
  const all = Array.from(REGISTRY.values());
  if (!filter.category) return all;
  return all.filter(w => w.categories.includes(filter.category));
}

export function allWidgetTypes() {
  return Array.from(REGISTRY.keys());
}

// Used by tests/test_widget_contracts.py via a thin export shim. Not for app code.
export function _dumpRegistryForTests() {
  return Array.from(REGISTRY.values()).map(w => ({
    type: w.type,
    categories: w.categories,
    palette_label: w.palette_label,
    description: w.description,
    publishes: w.publishes,
    subscribes: w.subscribes,
    actions: w.actions,
    isContainer: w.isContainer,
    group: w.group,
    has_component: typeof w.Component === 'function',
    has_config_schema: w.configSchema != null,
  }));
}
